import type { ApiClient } from './core';

export interface AdminSession {
  user: { name: string; email: string; initials: string };
  role: 'admin' | 'reviewer' | 'pharmacist_reviewer' | 'support';
  permissions: string[];
}
export interface NavCounts { live: number; review: number; dispatch: number; payments: number; notifications: number; banners: number }

/**
 * Admin console is large (70+ routes). Rather than hand-typing every
 * response shape (the per-page contracts live in `admin-dashboard.plan.md`
 * §3 and are the source of truth while building each page), this client
 * exposes one generic, strongly-typed-at-the-call-site method per resource
 * group plus the auth/session helpers used by every page.
 */
export function makeAdminApi(client: ApiClient) {
  return {
    // auth
    requestMagicLink: (email: string) => client.post<void>('/v1/admin/auth/magic-link', { email }),
    verifyMagicLink: (token: string, totp?: string) =>
      client.post<{ accessToken: string; refreshToken: string; expiresIn: number; totpRequired?: boolean }>('/v1/admin/auth/verify', { token, totp }),
    session: () => client.get<AdminSession>('/v1/admin/session'),
    navCounts: () => client.get<NavCounts>('/v1/admin/nav-counts'),
    search: (q: string) => client.get<{ items: unknown[] }>('/v1/admin/search', { q }),
    alerts: () => client.get<{ items: { id: string; text: string; tone: string; actionLabel?: string }[] }>('/v1/admin/alerts'),
    partnersHealth: () => client.get('/v1/admin/partners/health'),

    // overview
    overview: (range?: string, category?: string) => client.get('/v1/admin/overview', { range, category }),
    attention: () => client.get<{ items: unknown[] }>('/v1/admin/attention'),

    // live board
    board: (query?: Record<string, string | undefined>) => client.get('/v1/admin/board', query),

    // cart review
    reviewQueue: () => client.get<{ items: unknown[] }>('/v1/admin/review'),
    reviewItem: (id: string) => client.get(`/v1/admin/review/${id}`),
    reviewUpdate: (id: string, body: unknown) => client.patch(`/v1/admin/review/${id}`, body as any),
    reviewApprove: (id: string) => client.post(`/v1/admin/review/${id}/approve`),
    reviewReject: (id: string, reason: string) => client.post(`/v1/admin/review/${id}/reject`, { reason }),
    reviewRequestReupload: (id: string) => client.post(`/v1/admin/review/${id}/request-reupload`),
    reviewRxRequest: (id: string) => client.post(`/v1/admin/review/${id}/rx/request`),
    reviewRxVerify: (id: string, approved: boolean) => client.post(`/v1/admin/review/${id}/rx/verify`, { approved }),
    pricingSimulate: (body: unknown) => client.post('/v1/admin/pricing/simulate', body as any),

    // dispatch
    dispatchList: () => client.get<{ items: unknown[] }>('/v1/admin/dispatch'),
    dispatchDetail: (orderId: string) => client.get(`/v1/admin/dispatch/${orderId}`),
    dispatchBook: (orderId: string, partnerId?: string) => client.post(`/v1/admin/dispatch/${orderId}/book`, { partnerId }),
    dispatchRetry: (orderId: string) => client.post(`/v1/admin/dispatch/${orderId}/retry`),
    dispatchManual: (orderId: string, body: unknown) => client.post(`/v1/admin/dispatch/${orderId}/manual`, body as any),

    // orders
    orders: (query?: Record<string, string | undefined>) => client.get('/v1/admin/orders', query),
    ordersCsvUrl: (base: string, query?: Record<string, string | undefined>) => {
      const q = new URLSearchParams(query as Record<string, string>).toString();
      return `${base}/v1/admin/orders.csv${q ? `?${q}` : ''}`;
    },
    order: (id: string) => client.get(`/v1/admin/orders/${id}`),
    orderCancel: (id: string, reason?: string) => client.post(`/v1/admin/orders/${id}/cancel`, { reason }),
    orderRefund: (id: string, body: unknown) => client.post(`/v1/admin/orders/${id}/refunds`, body as any),
    orderNote: (id: string, note: string) => client.post(`/v1/admin/orders/${id}/notes`, { note }),
    orderRenotify: (id: string, channel?: string) => client.post(`/v1/admin/orders/${id}/renotify`, { channel }),

    // stores
    stores: (query?: Record<string, string | undefined>) => client.get('/v1/admin/stores', query),
    store: (id: string) => client.get(`/v1/admin/stores/${id}`),
    storeUpdate: (id: string, body: unknown) => client.patch(`/v1/admin/stores/${id}`, body as any),
    storePause: (id: string, reason?: string) => client.post(`/v1/admin/stores/${id}/pause`, { reason }),
    storeResume: (id: string) => client.post(`/v1/admin/stores/${id}/resume`),

    // customers
    customers: (q?: string) => client.get('/v1/admin/customers', { q }),
    customer: (id: string) => client.get(`/v1/admin/customers/${id}`),
    customerBlock: (id: string, reason?: string) => client.post(`/v1/admin/customers/${id}/block`, { reason }),
    customerUnblock: (id: string) => client.post(`/v1/admin/customers/${id}/unblock`),

    // platforms
    platforms: () => client.get<{ items: unknown[] }>('/v1/admin/platforms'),
    platformCreate: (body: unknown) => client.post('/v1/admin/platforms', body as any),
    platformUpdate: (id: string, body: unknown) => client.patch(`/v1/admin/platforms/${id}`, body as any),

    // partners / bookings
    partners: () => client.get<{ items: unknown[] }>('/v1/admin/partners'),
    partnerUpdate: (id: string, body: unknown) => client.patch(`/v1/admin/partners/${id}`, body as any),
    partnerHealthcheck: (id: string) => client.post(`/v1/admin/partners/${id}/healthcheck`),
    bookings: () => client.get('/v1/admin/bookings'),

    // payments
    paymentsSummary: () => client.get('/v1/admin/payments/summary'),
    transactions: (query?: Record<string, string | undefined>) => client.get('/v1/admin/transactions', query),
    transaction: (id: string) => client.get(`/v1/admin/transactions/${id}`),
    refunds: (status?: string) => client.get<{ items: unknown[] }>('/v1/admin/refunds', { status }),
    refundApprove: (id: string) => client.post(`/v1/admin/refunds/${id}/approve`),
    refundReject: (id: string, reason?: string) => client.post(`/v1/admin/refunds/${id}/reject`, { reason }),
    refundMarkPaid: (id: string) => client.post(`/v1/admin/refunds/${id}/mark-paid`),

    // notifications
    notifications: (query?: Record<string, string | undefined>) => client.get('/v1/admin/notifications', query),
    notificationResend: (id: string) => client.post(`/v1/admin/notifications/${id}/resend`),
    templates: () => client.get<{ items: unknown[] }>('/v1/admin/templates'),
    templateUpdate: (key: string, body: unknown) => client.put(`/v1/admin/templates/${key}`, body as any),
    templateReset: (key: string) => client.post(`/v1/admin/templates/${key}/reset`),

    // banners
    banners: () => client.get<{ items: unknown[] }>('/v1/admin/banners'),
    bannerCreate: (body: unknown) => client.post('/v1/admin/banners', body as any),
    bannerUpdate: (id: string, body: unknown) => client.patch(`/v1/admin/banners/${id}`, body as any),
    bannerDelete: (id: string) => client.delete(`/v1/admin/banners/${id}`),
    bannerReorder: (order: string[]) => client.post('/v1/admin/banners/reorder', { order }),
    upload: (body: unknown) => client.post<{ url: string }>('/v1/admin/uploads', body as any),

    // settings
    settings: (section: string) => client.get(`/v1/admin/settings/${section}`),
    settingsUpdate: (section: string, body: unknown, version: string) =>
      client.request(`PUT` as const, `/v1/admin/settings/${section}`, { body: body as any, ifMatch: version }),

    // team
    team: () => client.get<{ items: unknown[] }>('/v1/admin/team'),
    teamInvite: (body: { name: string; email: string; role: string }) => client.post('/v1/admin/team/invite', body as any),
    teamUpdate: (id: string, body: unknown) => client.patch(`/v1/admin/team/${id}`, body as any),
    teamRemove: (id: string) => client.delete(`/v1/admin/team/${id}`),

    // integrations
    integrations: () => client.get<{ items: unknown[] }>('/v1/admin/integrations'),
    integrationUpdate: (capability: string, body: unknown) => client.patch(`/v1/admin/integrations/${capability}`, body as any),
    integrationTest: (capability: string, provider: string) => client.post(`/v1/admin/integrations/${capability}/${provider}/test`),

    // applications
    applications: (query?: Record<string, string | undefined>) => client.get('/v1/admin/applications', query),
    application: (id: string) => client.get(`/v1/admin/applications/${id}`),
    applicationApprove: (id: string) => client.post(`/v1/admin/applications/${id}/approve`),
    applicationReject: (id: string, reason?: string) => client.post(`/v1/admin/applications/${id}/reject`, { reason }),
    applicationRequestInfo: (id: string, note: string) => client.post(`/v1/admin/applications/${id}/request-info`, { note }),

    audit: (query?: Record<string, string | undefined>) => client.get('/v1/admin/audit', query),
    providerCalls: (query?: Record<string, string | undefined>) => client.get('/v1/admin/provider-calls', query),
    reconciliation: () => client.get('/v1/admin/reconciliation'),

    stream: (onEvent: (e: string, d: unknown) => void) => client.sseStream('/v1/admin/stream', onEvent),
  };
}

export type AdminApi = ReturnType<typeof makeAdminApi>;
